import type {
  QuoteRequestInput,
  QuoteResponse,
  ChainMeta,
  AdapterMeta,
  TokenMeta,
  ExecutionStatus,
  ErrorResponse,
  WalletBalancesResponse,
} from "./types";

const BASE_URL =
  import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? "" : "https://api.llamaswap.app");
const API_KEY = import.meta.env.VITE_API_KEY ?? "";

class ApiError extends Error {
  status: number;
  body: ErrorResponse | null;

  constructor(status: number, body: ErrorResponse | null) {
    super(body?.message ?? `API error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(API_KEY ? { "X-API-Key": API_KEY } : {}),
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers as Record<string, string>) },
  });

  if (!res.ok) {
    let body: ErrorResponse | null = null;
    try {
      body = (await res.json()) as ErrorResponse;
    } catch {
      // ignore parse errors
    }
    throw new ApiError(res.status, body);
  }

  return res.json() as Promise<T>;
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number] => entry[1] != null,
  );
  if (entries.length === 0) return "";
  return "?" + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

export const api = {
  getQuote(params: QuoteRequestInput): Promise<QuoteResponse> {
    return request("/v1/quote", {
      method: "POST",
      body: JSON.stringify(params),
    });
  },

  getChains(): Promise<{ chains: ChainMeta[]; adapters: AdapterMeta[] }> {
    return request("/v1/chains");
  },

  getTokens(chainId: string, search?: string): Promise<{ chainId: string; tokens: TokenMeta[] }> {
    return request(`/v1/tokens${toQuery({ chainId, search })}`);
  },

  lookupToken(chainId: string, address: string): Promise<TokenMeta & { verified: boolean }> {
    return request(`/v1/tokens/lookup${toQuery({ chainId, address })}`);
  },

  getStatus(adapter: string, routeId: string, depositTx?: string): Promise<ExecutionStatus> {
    return request(`/v1/status${toQuery({ adapter, routeId, depositTx })}`);
  },

  getBalances(address: string): Promise<WalletBalancesResponse> {
    return request(`/v1/balances/${address}`);
  },
};

export { ApiError };
export { BASE_URL, API_KEY };
